import apiClient from './apiClient';
import { TRACK_EVENTS } from '../config/trackEventConfig';
import logger from '../utils/logger';

/**
 * 埋点管理类
 */
class TrackingManager {
  constructor() {
    this.platformId = 2;
    this.isInitialized = false;
    this.pendingEvents = [];
  }

  async init() {
    if (this.isInitialized) return;
    
    this.userId = this.getUserId();
    this.ip = await this.getClientIP();
    this.networkType = this.getNetworkType();
    
    this.isInitialized = true;
    logger.log('[TrackingManager] 初始化成功');
    
    if (this.pendingEvents.length > 0) {
      await this.processPendingEvents();
    }

    // 启动定期重试机制
    this.startRetryTimer();
  }

  startRetryTimer() {
    // 每300秒检查一次待上报队列
    this.retryTimer = setInterval(() => {
      if (this.pendingEvents.length > 0) {
        logger.log('[TrackingManager] 定期重试，待上报事件数:', this.pendingEvents.length);
        this.processPendingEvents();
      }
    }, 300000); // 300秒
  }

  stopRetryTimer() {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
  }

  getUserId() {
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        return user.id || 0;
      }
    } catch (error) {
      logger.warn('[TrackingManager] 解析用户信息失败:', error);
    }
    return 0;
  }

  async getClientIP() {
    try {
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 3000);
      const response = await fetch('https://api.ipify.org?format=json', { signal: ctrl.signal });
      clearTimeout(timeout);
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  }

  getNetworkType() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      if (connection.effectiveType) return connection.effectiveType;
      if (connection.type) return connection.type;
    }
    return 'unknown';
  }

  prepareTrackingData(eventConfig, extraData = {}) {
    return {
      ip: this.ip,
      userId: this.userId,
      networkType: this.networkType,
      platformId: this.platformId,
      eventId: eventConfig.eventId,
      eventDescription: eventConfig.description,
      content: extraData.content || '',
      p1: extraData.p1 || '',
      p2: extraData.p2 || '',
      p3: extraData.p3 || '',
      reportTime: new Date().toISOString() // 新增：上报时间
    };
  }

  reportEvent(eventKey, extraData = {}) {
    // 异步执行，不等待结果
    this._reportEventAsync(eventKey, extraData);
  }

  async _reportEventAsync(eventKey, extraData = {}) {
    try {
      if (!this.isInitialized) {
        this.pendingEvents.push({ eventKey, extraData });
        await this.init();
        return;
      }

      const eventConfig = TRACK_EVENTS[eventKey];
      if (!eventConfig) {
        logger.log('[TrackingManager] 未找到事件配置:', eventKey);
        return false;
      }

      const trackingData = this.prepareTrackingData(eventConfig, extraData);
      const response = await apiClient.post('/tracking', trackingData);
      
      if (response.success) {
        logger.log('[TrackingManager] 埋点成功:', eventKey);
        return true;
      } else {
        logger.error('[TrackingManager] 埋点失败:', eventKey, response.message);
        // 失败时加入待上报队列
        this.addToPendingQueue(eventKey, extraData);
        return false;
      }
    } catch (error) {
      logger.error('[TrackingManager] 埋点异常:', eventKey, error);
      // 异常时也加入待上报队列
      this.addToPendingQueue(eventKey, extraData);
      return false;
    }
  }

  addToPendingQueue(eventKey, extraData) {
    this.pendingEvents.push({ eventKey, extraData });
    logger.log('[TrackingManager] 事件加入待上报队列:', eventKey, '队列长度:', this.pendingEvents.length);
    
    // 如果队列过长，清理一些旧事件
    if (this.pendingEvents.length > 100) {
      this.pendingEvents = this.pendingEvents.slice(-50);
      logger.log('[TrackingManager] 清理待上报队列，保留最新50个事件');
    }
  }

  async processPendingEvents() {
    if (this.pendingEvents.length === 0) return;
    
    const events = [...this.pendingEvents];
    this.pendingEvents = [];
    
    logger.log('[TrackingManager] 开始处理待上报事件，数量:', events.length);
    
    const results = [];
    for (const { eventKey, extraData } of events) {
      try {
        const eventConfig = TRACK_EVENTS[eventKey];
        if (!eventConfig) {
          logger.error('[TrackingManager] 未找到事件配置:', eventKey);
          continue;
        }

        const trackingData = this.prepareTrackingData(eventConfig, extraData);
        const response = await apiClient.post('/tracking', trackingData);
        
        if (response.success) {
          logger.log('[TrackingManager] 重试埋点成功:', eventKey);
          results.push({ eventKey, success: true });
        } else {
          logger.error('[TrackingManager] 重试埋点失败:', eventKey, response.message);
          // 失败的事件重新加入队列
          this.pendingEvents.push({ eventKey, extraData });
          results.push({ eventKey, success: false });
        }
      } catch (error) {
        logger.error('[TrackingManager] 重试埋点异常:', eventKey, error);
        // 异常的事件重新加入队列
        this.pendingEvents.push({ eventKey, extraData });
        results.push({ eventKey, success: false });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    const failCount = results.length - successCount;
    
    logger.log('[TrackingManager] 待上报事件处理完成，成功:', successCount, '失败:', failCount);
    
    // 如果还有失败的事件，且队列长度超过20，清理一些旧事件
    if (this.pendingEvents.length > 20) {
      const oldLength = this.pendingEvents.length;
      this.pendingEvents = this.pendingEvents.slice(-10);
      logger.log('[TrackingManager] 清理待上报队列，从', oldLength, '减少到', this.pendingEvents.length);
    }
  }

  // 业务埋点方法
  reportDAU() {
    this.reportEvent('NAV_DAU', {
      content: '用户访问导航网站',
      p1: 'daily_active_user',
      p2: new Date().toISOString().split('T')[0]
    });
  }

  reportWebsiteClick(website) {
    this.reportEvent('NAV_WEBSITE_CLICK', {
      content: website.url || '',
      p1: website.name || website.title || '',
      p2: website.id || '',
      p3: 'navigation_click'
    });
  }

  reportPageView(pageName) {
    this.reportEvent('NAV_PAGE_VIEW', {
      content: pageName,
      p1: window.location.pathname,
      p2: document.title
    });
  }

  reportUserAction(action, target, details = '') {
    this.reportEvent('NAV_USER_ACTION', {
      content: details,
      p1: action,
      p2: target
    });
  }

  reportError(error, context = '') {
    this.reportEvent('NAV_ERROR', {
      content: error.stack || error.toString(),
      p1: context,
      p2: error.name || 'Unknown'
    });
  }

  reportSearch(keyword, results = '') {
    this.reportEvent('NAV_SEARCH', {
      content: keyword,
      p1: 'search_keyword',
      p2: results
    });
  }

  reportSettingsChange(setting, value) {
    this.reportEvent('NAV_SETTINGS_CHANGE', {
      content: `${setting}: ${value}`,
      p1: setting,
      p2: value
    });
  }

  reportThemeChange(theme) {
    this.reportEvent('NAV_THEME_CHANGE', {
      content: `主题切换为: ${theme}`,
      p1: theme,
      p2: 'theme_change'
    });
  }

  reportWallpaperChange(wallpaper) {
    this.reportEvent('NAV_WALLPAPER_CHANGE', {
      content: `壁纸切换为: ${wallpaper}`,
      p1: wallpaper,
      p2: 'wallpaper_change'
    });
  }

  reportCategoryAction(action, categoryName) {
    const eventKey = `NAV_CATEGORY_${action.toUpperCase()}`;
    this.reportEvent(eventKey, {
      content: `分类${action}: ${categoryName}`,
      p1: categoryName,
      p2: action
    });
  }

  reportWebsiteAction(action, websiteName) {
    const eventKey = `NAV_WEBSITE_${action.toUpperCase()}`;
    this.reportEvent(eventKey, {
      content: `网站${action}: ${websiteName}`,
      p1: websiteName,
      p2: action
    });
  }

  reportUserAuth(action, email = '') {
    const eventKey = `NAV_USER_${action.toUpperCase()}`;
    this.reportEvent(eventKey, {
      content: `用户${action}`,
      p1: email,
      p2: action
    });
  }

  reportSync(action, details = '') {
    const eventKey = `NAV_SYNC_${action.toUpperCase()}`;
    this.reportEvent(eventKey, {
      content: details,
      p1: action,
      p2: 'sync_operation'
    });
  }

  reportPerformance(metric, value) {
    this.reportEvent('NAV_PERFORMANCE', {
      content: `${metric}: ${value}`,
      p1: metric,
      p2: value.toString()
    });
  }

  // 工具方法
  getPendingEventsCount() {
    return this.pendingEvents.length;
  }

  getPendingEvents() {
    return [...this.pendingEvents];
  }

  clearPendingEvents() {
    const count = this.pendingEvents.length;
    this.pendingEvents = [];
    logger.log('[TrackingManager] 清空待上报队列，清理事件数:', count);
  }

  // 手动触发重试
  async retryPendingEvents() {
    if (this.pendingEvents.length > 0) {
      logger.log('[TrackingManager] 手动触发重试，待上报事件数:', this.pendingEvents.length);
      await this.processPendingEvents();
    } else {
      logger.log('[TrackingManager] 没有待上报事件');
    }
  }

  // 销毁方法
  destroy() {
    this.stopRetryTimer();
    this.pendingEvents = [];
    this.isInitialized = false;
    logger.log('[TrackingManager] 已销毁');
  }
}

const trackingManager = new TrackingManager();
export default trackingManager; 